import { useEffect, useState } from "react";
import api from "@/lib/api";
import ProgressBar from "@/components/ProgressBar";

export default function Progress() {
    const [summary, setSummary] = useState(null);
    const [modules, setModules] = useState([]);
    const [attempts, setAttempts] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/progress/summary").then((r) => r.data),
            api.get("/modules").then((r) => r.data),
            api.get("/quiz-attempts/me").then((r) => r.data),
        ]).then(([s, m, a]) => { setSummary(s); setModules(m); setAttempts(a); });
    }, []);

    if (!summary) return <div className="text-sm text-ink-500" data-testid="progress-loading">Loading…</div>;

    return (
        <div className="space-y-10" data-testid="progress-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Personal analytics</div>
                <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">Your progress.</h1>
            </div>

            <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-300 border border-ink-300">
                <Stat label="Certification" value={`${summary.certification_pct}%`} />
                <Stat label="Lesson completion" value={`${summary.lesson_pct}%`} />
                <Stat label="Quiz attempts" value={`${summary.attempts}`} />
                <Stat label="Quizzes passed" value={`${summary.passed_quizzes}/${summary.total_quizzes}`} />
            </section>

            <section>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-4">By module</div>
                <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                    {modules.length === 0 && <div className="px-6 py-6 text-sm text-ink-500" data-testid="progress-modules-empty">No modules yet.</div>}
                    {modules.map((m) => (
                        <div key={m.id} className="px-6 py-4 grid md:grid-cols-[1fr_240px_60px] gap-6 items-center" data-testid={`progress-module-${m.id}`}>
                            <div className="font-heading text-base text-ink-900">{m.title}</div>
                            <ProgressBar value={m.progress_pct ?? 0} size="sm" />
                            <div className="text-sm text-ink-500 font-mono text-right">{m.progress_pct ?? 0}%</div>
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-4">Recent attempts</div>
                {attempts.length === 0 ? (
                    <div className="text-sm text-ink-500" data-testid="progress-attempts-empty">No quiz attempts yet.</div>
                ) : (
                    <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                        {attempts.slice(0, 12).map((a) => (
                            <div key={a.id} className="px-6 py-3 flex items-center justify-between text-sm" data-testid={`progress-attempt-${a.id}`}>
                                <div className="text-ink-900">{a.correct}/{a.total} correct</div>
                                <div className="text-xs text-ink-500">{new Date(a.submitted_at).toLocaleString()}</div>
                                <div className={`font-mono text-xs ${a.passed ? "text-haevn-700" : "text-clay-500"}`}>{a.score}%</div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{label}</div>
            <div className="mt-3 font-heading text-3xl text-ink-900">{value}</div>
        </div>
    );
}
